import { useState, useRef } from 'react'
import { Stack, Button, Text, Progress } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconUpload, IconCheck, IconX } from '@tabler/icons-react'
import { usePhotos } from '../../hooks/usePhotos'
import styles from './PhotoUpload.module.css'

interface PhotoUploadProps {
  guestId: string
  guestPin?: string
  onUploadSuccess?: () => void
}

/**
 * Photo upload component for guests
 * Supports batch file selection from device and direct camera capture on mobile
 */
export function PhotoUpload({ guestId, guestPin, onUploadSuccess }: PhotoUploadProps) {
  const { uploadPhoto, uploading } = usePhotos()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [totalFiles, setTotalFiles] = useState(0)

  const handleBatchUpload = async (files: File[]) => {
    if (files.length === 0) return

    setTotalFiles(files.length)
    setUploadingCount(0)
    let successCount = 0
    let errorCount = 0

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!file) continue
      
      try {
        // Update progress for current file
        const baseProgress = (i / files.length) * 100
        setUploadProgress(baseProgress + 5)
        setUploadingCount(i + 1)

        // Upload photo
        const result = await uploadPhoto(guestId, file, guestPin)

        if (result.success) {
          successCount++
        } else {
          errorCount++
          console.error(`Upload failed for ${file.name}:`, result.error)
        }

        // Update progress
        setUploadProgress(((i + 1) / files.length) * 100)
      } catch (error) {
        errorCount++
        console.error(`Upload error for ${file.name}:`, error)
      }
    }

    // Show summary notification
    if (successCount > 0 && errorCount === 0) {
      const message = `${successCount} Fotos wurden erfolgreich hochgeladen.`
      notifications.show({
        title: 'Erfolgreich hochgeladen',
        message,
        color: 'green',
        icon: <IconCheck size={20} />,
      })
    } else if (successCount > 0 && errorCount > 0) {
      const message = `${successCount} von ${files.length} Fotos wurden hochgeladen.`
      notifications.show({
        title: 'Teilweise erfolgreich',
        message,
        color: 'yellow',
        icon: <IconCheck size={20} />,
      })
    } else {
      notifications.show({
        title: 'Fehler',
        message: 'Fotos konnten nicht hochgeladen werden.',
        color: 'red',
        icon: <IconX size={20} />,
      })
    }

    // Reset progress after animation
    setTimeout(() => {
      setUploadProgress(0)
      setUploadingCount(0)
      setTotalFiles(0)
    }, 2000)

    // Notify parent component if any uploads succeeded
    if (successCount > 0 && onUploadSuccess) {
      onUploadSuccess()
    }
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const fileArray = Array.from(files)
      handleBatchUpload(fileArray)
    }
    // Reset input
    if (event.target) {
      event.target.value = ''
    }
  }

  return (
    <Stack gap="md" className={styles.container}>
      {/* Upload Button */}
      <Button
        leftSection={<IconUpload size={20} />}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        size="md"
        className={styles.uploadButton}
        fullWidth
      >
        {totalFiles > 0
          ? `${uploadingCount} von ${totalFiles} Fotos hochladen...`
          : 'Fotos auswählen'}
      </Button>

      {/* Hidden File Input - supports multiple files and camera capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        capture="environment"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        aria-label="Fotos auswählen oder mit Kamera aufnehmen"
      />

      {/* Upload Progress */}
      {uploadProgress > 0 && (
        <Stack gap="xs">
          <Progress value={uploadProgress} animated className={styles.progress} />
          <Text size="sm" c="dimmed" ta="center">
            {uploadProgress < 100
              ? totalFiles > 1
                ? `Hochladen... ${uploadingCount} von ${totalFiles}`
                : 'Hochladen...'
              : 'Fertig!'}
          </Text>
        </Stack>
      )}

      {/* Info Text */}
      <Text size="sm" c="dimmed" ta="center">
        Maximale Dateigröße: 10 MB • Erlaubte Formate: JPEG, PNG, WebP, HEIC
      </Text>
    </Stack>
  )
}

